import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { Backdrop, Box, Button, Card, CardContent, CardMedia, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, ListItemText, MenuItem, Select, TextField, Tooltip, Typography } from "@mui/material";
import { getPlatformAndPromotion, getRelatedPromotions, platformRatesListFromCreator, platformRatesListFromSelectedKeys, platformToFollowing, platformToLink, platPromoToKey, promotionLabelsFromList, USAGE_RIGHTS_OPTIONS } from "../Utils/constants";
import { useMutation, useQuery } from "react-query";
import API from "../API";
import { RateField } from "./RateField";
import { useEffect, useState } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import noPfp from './globalAssets/ppfLogo.png'
import useAlertDialog from './useAlertDialog'
import AlertDialog from './AlertDialog'
import { toast } from 'sonner';

function VideoDetails({
    creator,
    video,
    index,
    promotions,
    handleOptionChange,
    handleRemoveVideo,
    videoAmount,
    triedNext
}) {
    const crossPostOptions = platformRatesListFromSelectedKeys(
        creator,
        getRelatedPromotions(video.rateKey)
    );

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    minWidth: '0px',
                    maxWidth: '100%',
                }}
            >
                <IconButton
                    sx={{ mx: 2 }}
                    disabled={videoAmount <= 1}
                    onClick={() => handleRemoveVideo(index)}
                >
                    <RemoveCircleOutlineIcon />
                </IconButton>

                <Box sx={{ flex: 1, minWidth: '0px' }}>
                    <TextField
                        value={video.rateKey}
                        onChange={(e) =>
                            handleOptionChange(index, 'rateKey', e.target.value)
                        }
                        label="Promotion Type"
                        select
                        fullWidth
                        variant="outlined"
                        size="small"
                        sx={{ mb: 1 }}
                        error={triedNext && !video.rateKey}
                        helperText={
                            triedNext && !video.rateKey
                                ? 'Select a promotion for this video'
                                : ''
                        }
                    >
                        {promotions.map((promo) => (
                            <MenuItem
                                key={`${video.id}-${promo.rateKey}`}
                                value={promo.rateKey}
                            >
                                {promo.label}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        label="Crossposting"
                        select
                        fullWidth
                        variant="outlined"
                        size="small"
                        sx={{ mb: 1 }}
                        SelectProps={{
                            multiple: true,
                            renderValue: (selected) => promotionLabelsFromList(selected).join(', '),
                        }}
                        value={[...video.crossPostingKeys]}
                        onChange={(e) =>
                            handleOptionChange(
                                index,
                                'crossPostingKeys',
                                e.target.value,
                            )
                        }
                    >
                        {crossPostOptions.map((promo) => (
                            <MenuItem
                                key={`${video.id}-cross-${promo.rateKey}`}
                                value={promo.rateKey}
                            >
                                <Checkbox checked={video.crossPostingKeys.has(promo.rateKey)} />
                                <ListItemText
                                    primaryTypographyProps={{ sx: { fontWeight: 'normal !important' } }}
                                    primary={promo.label}
                                />
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>
            </Box>

            <Divider variant="inset" flexItem sx={{ mb: 2 }} />
        </>
    );
}

const hasInvalidRateKey = (videos) =>
    videos.some((video) => !video.rateKey || video.rateKey.trim() === '');


export default function CampaignVideoEditor({ campaignId, creator, isOpen, onClose }) {
    // ---------- State
    const [triedNext, setTriedNext] = useState(false);
    const [creatorEdited, setCreatorEdited] = useState(null);

    // -----------  Hooks ---------------

    const { dialogState, openDialog, closeDialog } = useAlertDialog();

    const { data: campaignData, isLoading: campaignLoading, error: campaignError } = useQuery({
        queryKey: ["campaign", campaignId],
        queryFn: () => API.campaigns.fetch(campaignId),
        enabled: isOpen&&!!campaignId,
        refetchOnWindowFocus:false
    });

    const { mutate: campaignUpdate, isLoading: updateLoading } = useMutation({
        mutationFn: API.campaigns.updateCampaignCreatorSpecs,
        onError: (error) => {
            openDialog('Error', `Error updating creator videos: ${error?.response?.data?.error || error.message}`);
        },
        onSuccess: () => {
            const doClose = () => {
                closeDialog();
                onClose();
            }
            openDialog("Sucess", "Data updated sucessfully", doClose, doClose, "Ok");
        }
    });

    useEffect(() => {
        if (campaignError) {
            const doClose = () => {
                closeDialog();
                onClose();
            }
            openDialog("Error", `Error updating creator videos: ${campaignError?.response?.data?.error || campaignError.message}`, doClose, doClose, "Ok");
        }

    }, [campaignError]);

    const creatorData = campaignData?.creators.find(currCreator => currCreator.id == creator);

    useEffect(() => {
        if (creatorData) {
            setCreatorEdited(
                {
                    ...creatorData.creator_details,
                    ...{
                        ...creatorData,
                        creator_details: null
                    },
                    basePrice: creatorData.price,
                    usageRightsKey: '',
                    adjustedPrice: creatorData.price,
                    videos: creatorData.videos
                        .filter((video) => video.parentId == null)
                        .map((video) => ({
                            ...video,
                            rateKey: platPromoToKey[video.promotionPlatform][video.promotionType],
                            crossPostingKeys: new Set([...creatorData.videos
                                .filter((cross) => cross.parentId === video.id)
                                .map((cross) => (platPromoToKey[cross.promotionPlatform][cross.promotionType]))]),
                        }))
                }
            )
        }

    }, [campaignData]);


    // -------- Derived values -----------
    const promotions = platformRatesListFromCreator(creatorData?.creator_details);
    const hasEmptyKey = !!creatorEdited ? hasInvalidRateKey(creatorEdited?.videos) : false;
    const isBelowMin = false;

    // --------- Functions

    const calculateUsageRightsCost = (basePrice, usageRightsKey, usageRightsOverride) => {
        if (!usageRightsKey) return 0;
        const recommended = basePrice * (USAGE_RIGHTS_OPTIONS[usageRightsKey]?.recommendedPercentage || 0);
        return usageRightsOverride !== undefined ? usageRightsOverride : recommended;
    };

    const calculateBasePrice = (creator, videos = []) => {

        const firstAmount = videos.reduce((accum, video) => {
            const videoBase = creator[video.rateKey] || 0;
            const crossPostCost = Array.from(video.crossPostingKeys).reduce((sum, crossKey) => {
                const cost = creator[crossKey] || 0;
                return sum + Math.max(cost / 4, 0);
            }, 0);
            return accum + Math.max(videoBase, 0) + crossPostCost;
        }, 0);
        return firstAmount + calculateUsageRightsCost(firstAmount, creator.usageRightsKey);
    };

    const handlePriceChange = (newPrice) => {
        setCreatorEdited((prev) => {
            const creator = { ...prev };
            const numericPrice = Math.max(0, parseFloat(newPrice) || 0);
            creator.basePrice = numericPrice;

            creator.adjustedPrice = numericPrice + calculateUsageRightsCost(
                numericPrice,
                creator.usageRightsKey
            );
            return creator;
        });
    };

    const getNextId = (videos) => {
        const highestId = [...videos, ...creatorData.videos].reduce((max, video) => Math.max(max, video.id), 0);
        return highestId + 1;
    };

    const handleAddVideo = () => {
        setCreatorEdited((prev) => {
            const updatedCreator = { ...prev };
            const nextId = getNextId(updatedCreator.videos);

            toast.info('Adding new video', {
                description: `Video ID: ${nextId}`
            });

            const updatedVideos = [
                ...updatedCreator.videos,
                {
                    id: nextId,
                    rateKey: '',
                    crossPostingKeys: new Set([]),
                },
            ];

            updatedCreator.videos = updatedVideos;
            return updatedCreator;
        });
    };

    const handleRemoveVideo = (index) => {
        setCreatorEdited((prev) => {
            const updatedCreator = { ...prev };
            updatedCreator.videos = updatedCreator.videos.filter((_, i) => i !== index);
            return updatedCreator;
        });
    };

    const handleOptionChange = (index, option, value) => {
        setCreatorEdited((prev) => {
            const updatedCreator = { ...prev };

            switch (option) {
                case 'usageRightsKey':
                case 'basePrice': {
                    updatedCreator[option] = value;
                    break;
                }
                default: {
                    const updatedVideos = [...updatedCreator.videos];
                    const updatedVideo = { ...updatedVideos[index] };

                    if (option === 'crossPostingKeys') {
                        updatedVideo[option] = new Set([...value]);
                    } else {
                        if (option === 'rateKey') {
                            updatedVideo.crossPostingKeys = new Set();
                        }
                        updatedVideo[option] = value;
                    }
                    updatedVideos[index] = updatedVideo;
                    updatedCreator.videos = updatedVideos;
                    break;
                }
            }

            updatedCreator.basePrice = calculateBasePrice(updatedCreator, updatedCreator.videos);
            updatedCreator.adjustedPrice = updatedCreator.basePrice + calculateUsageRightsCost(
                updatedCreator.basePrice,
                updatedCreator.usageRightsKey
            );

            return updatedCreator;
        });
    };

    const SubmitChanges = () => {
        const nextId = getNextId(creatorEdited.videos);
        let crossPostOffset = 0;
        const campaigMods = {
            campaignId,
            creators: [
                {
                    id: creatorEdited.creator,
                    name: creatorEdited.creator,
                    price: creatorEdited.adjustedPrice,
                    agencyRate: creatorEdited.adjustedPrice * 0.95,
                    videos: [
                        ...creatorEdited.videos.map((video) => {
                            const { platform, promotion } = getPlatformAndPromotion(video.rateKey);
                            const existingVideo = creatorData.videos.find(otherVid => otherVid.id == video.id);
                            return ({
                                ...(!!existingVideo && existingVideo),
                                id: video.id,
                                parentId: null,
                                promotionPlatform: platform,
                                promotionType: promotion,
                                platformLink: creatorEdited[platformToLink[platform]],
                                following: creatorEdited[platformToFollowing[platform]],
                            })
                        }),
                        ...creatorEdited.videos.flatMap((video) => {
                            const result = [...video.crossPostingKeys].map((crossPost, index) => {
                                const { platform, promotion } = getPlatformAndPromotion(crossPost);
                                const existingVideo = creatorData.videos.find(
                                    otherVid => otherVid.promotionPlatform == platform && otherVid.promotionType == promotion &&
                                        otherVid.parentId === video.id
                                )
                                const newCrosspost = ({
                                    id: nextId + crossPostOffset,
                                    parentId: video.id,
                                    promotionPlatform: platform,
                                    promotionType: promotion,
                                    platformLink: creatorEdited[platformToLink[platform]],
                                    following: creatorEdited[platformToFollowing[platform]],
                                    ...existingVideo
                                })
                                crossPostOffset += existingVideo ? 0 : 1
                                return newCrosspost;
                            })

                            return result;
                        }
                        )
                    ]
                }
            ]
        }
        campaignUpdate(campaigMods);
    }



    return (
        <>
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }} open={campaignLoading || updateLoading}>
                <CircularProgress color='inherit'></CircularProgress>
            </Backdrop>
            <Dialog open={!!campaignData && isOpen} maxWidth='sm' sx={{
                minWidth: '90vw'
            }}>
                <DialogTitle>
                    Video for Creator: {creatorEdited?.id}
                </DialogTitle>
                <DialogContent
                    dividers
                    sx={{
                        width: '100%',
                        flex: 1, minWidth: '0px', display: 'flex', flexDirection: 'row', gap: 2,
                        paddingBlockEnd: 1,
                    }}>
                    <Box sx={{ width: '25%', display: { xs: 'none', md: 'block' } }}>
                        <CardMedia
                            component="img"
                            image={creatorEdited?.pfphref || noPfp}
                            alt={creatorEdited?.id}
                            sx={{
                                width: '100%',
                                aspectRatio: '1/1',
                                objectFit: 'cover',
                                borderRadius: (theme) => `${theme.shape.borderRadius}px`,
                            }}
                        />
                    </Box>

                    <Box sx={{ width: { xs: '100%', md: '70%' } }}>
                        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row' }}>
                            <TextField
                                size="small"
                                disabled
                                fullWidth
                                sx={{ mb: 2 }}
                                value={`${creatorEdited?.videos.length || 0} Video${creatorEdited?.videos.length === 1 ? '' : 's'}`}
                            />
                            <Tooltip
                                title={
                                    hasEmptyKey
                                        ? 'Finish filling the current video(s) first'
                                        : 'Add another video'
                                }
                            >
                                <span>
                                    <IconButton
                                        disabled={hasEmptyKey}
                                        onClick={() => !hasEmptyKey && handleAddVideo()}
                                    >
                                        <AddCircleOutlineIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                        {creatorEdited?.videos.map((video, index) => (
                            <VideoDetails
                                key={`${creatorEdited.id}-videos-${video.id}`}
                                video={video}
                                creator={creatorEdited}
                                promotions={promotions}
                                handleOptionChange={handleOptionChange}
                                handleRemoveVideo={handleRemoveVideo}
                                triedNext={triedNext}
                                index={index}
                                videoAmount={creatorEdited.videos.length}
                            />
                        ))}

                        <RateField
                            error={triedNext && (!creatorEdited?.basePrice || creatorEdited?.basePrice === 0)}
                            helperText={
                                isBelowMin ? (
                                    <Typography color="error">
                                        Warning: Below recommended minimum of ${(0).toFixed(2)} (70% of original)
                                    </Typography>
                                ) : triedNext && (!creatorEdited?.basePrice || creatorEdited?.basePrice === 0) ? (
                                    'Enter a rate for this creator'
                                ) : (
                                    ''
                                )
                            }
                            label="Base Price (override)"
                            variant="outlined"
                            fullWidth
                            size="small"
                            value={creatorEdited?.basePrice || '0'}
                            onChange={(value) => handlePriceChange(value)}
                            sx={{ mb: 2 }}
                        />

                        <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ marginInlineEnd: 2 }}>
                                Usage Rights:
                            </Typography>
                            <Select
                                value={creatorEdited?.usageRightsKey || ''}
                                onChange={(e) => handleOptionChange(0, 'usageRightsKey', e.target.value)}
                                fullWidth
                                size="small"
                            >
                                <MenuItem value="">None</MenuItem>
                                {Object.entries(USAGE_RIGHTS_OPTIONS).map(([key, { label }]) => (
                                    <MenuItem key={key} value={key}>
                                        {label}
                                    </MenuItem>
                                ))}
                            </Select>
                            <Typography variant="subtitle2" color='text.secondary' sx={{
                                marginInline: 1
                            }}>
                                (Adds a suggested rate)
                            </Typography>
                        </Box>

                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="body2" color='text.secondary'>
                                    Old total: ${creatorData?.price.toFixed(2)}
                                </Typography>
                                <Typography variant="body2" color='text.secondary' sx={{ fontWeight: 'bold' }}>
                                    New total: ${creatorEdited?.adjustedPrice.toFixed(2)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Box>

                </DialogContent>
                <DialogActions>
                    <Button variant="text" disabled={updateLoading} color='secondary' onClick={()=> onClose()}>
                        Cancel
                    </Button>
                    <Button variant="contained" disabled={updateLoading} onClick={() => SubmitChanges()}>
                        Save Changes (${creatorEdited?.adjustedPrice.toFixed(2)})
                    </Button>
                </DialogActions>
            </Dialog>

            <AlertDialog alertState={dialogState}></AlertDialog>
        </>
    )
} 